import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Landmark,
  ArrowDownToLine,
  ArrowUpFromLine,
  BookOpen,
  DollarSign,
  Loader2,
} from "lucide-react";
import { trustApi, bankAccountsApi } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import TrustOverviewTab from "./TrustOverviewTab";
import TrustReceiptsTab from "./TrustReceiptsTab";
import TrustDisbursementsTab from "./TrustDisbursementsTab";
import TrustLedgerTab from "./TrustLedgerTab";

interface BankAccount {
  _id: string;
  kind: string;
  name: string;
  institution: string;
  accountLast4: string;
  status: string;
}

interface SummaryData {
  totalTrustBalance: number;
  pendingDeposits: number;
  pendingDisbursements: number;
  pendingPayouts: number;
}

const tabs = [
  { key: "overview", label: "Overview", Icon: Landmark },
  { key: "receipts", label: "Receipts", Icon: ArrowDownToLine },
  { key: "disbursements", label: "Disbursements", Icon: ArrowUpFromLine },
  { key: "ledger", label: "Ledger", Icon: BookOpen },
];

function formatCents(cents: number): string {
  const isNegative = cents < 0;
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return isNegative ? `-${formatted}` : formatted;
}

/**
 * Trust accounts content  -  embeddable without outer page wrapper.
 * Used by AccountingPage as the "Trust Accounts" tab.
 */
export const TrustAccountsContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [trustAccounts, setTrustAccounts] = useState<BankAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [summary, setSummary] = useState<SummaryData>({
    totalTrustBalance: 0,
    pendingDeposits: 0,
    pendingDisbursements: 0,
    pendingPayouts: 0,
  });
  const [loading, setLoading] = useState(true);

  // Load trust bank accounts
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const res = await bankAccountsApi.list({ kind: "trust", status: "open" });
        const accounts = res.data?.data || [];
        setTrustAccounts(accounts);
        if (accounts.length > 0 && !selectedAccountId) {
          setSelectedAccountId(accounts[0]._id);
        }
      } catch (err) {
        console.error("Failed to load trust accounts:", err);
      }
    };
    loadAccounts();
  }, []);

  // Load summary data
  const loadSummary = useCallback(async () => {
    if (!selectedAccountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [subledgerRes, pendingReceiptsRes, pendingDisbRes] = await Promise.all([
        trustApi.getSubledgerSummary({ bankAccountId: selectedAccountId }),
        trustApi.listReceipts({ bankAccountId: selectedAccountId, status: "pending", limit: 1 }),
        trustApi.listDisbursements({ bankAccountId: selectedAccountId, status: "pending_approval", limit: 1 }),
      ]);

      const subledgerData = subledgerRes.data?.data || [];
      const totalTrustBalance = subledgerData.reduce(
        (sum: number, item: any) => sum + (item.totalLedgerBalanceCents || 0),
        0
      );

      setSummary({
        totalTrustBalance,
        pendingDeposits: pendingReceiptsRes.data?.pagination?.total || 0,
        pendingDisbursements: pendingDisbRes.data?.pagination?.total || 0,
        pendingPayouts: 0,
      });
    } catch (err) {
      console.error("Failed to load summary:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const summaryCards = [
    {
      label: "Total Trust Balance",
      value: formatCents(summary.totalTrustBalance),
      Icon: DollarSign,
      color: "text-emerald-700",
      bg: "bg-emerald-50",
    },
    {
      label: "Pending Deposits",
      value: summary.pendingDeposits.toString(),
      Icon: ArrowDownToLine,
      color: "text-amber-700",
      bg: "bg-amber-50",
    },
    {
      label: "Pending Disbursements",
      value: summary.pendingDisbursements.toString(),
      Icon: ArrowUpFromLine,
      color: "text-blue-700",
      bg: "bg-blue-50",
    },
  ];

  return (
    <>
      {/* Trust Account Selector */}
      <div className="flex justify-end mb-6">
        <div className="min-w-[280px]">
          <SearchableSelect
            options={trustAccounts.length === 0
              ? [{ value: "", label: "No trust accounts" }]
              : trustAccounts.map((acct) => ({
                  value: acct._id,
                  label: `${acct.name}${acct.accountLast4 ? ` (...${acct.accountLast4})` : ""}`,
                }))
            }
            value={selectedAccountId || null}
            onChange={(val) => setSelectedAccountId(val || "")}
            placeholder="Select trust account"
            icon={Landmark}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summaryCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-white rounded-lg shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${card.bg}`}>
                <card.Icon className={`h-5 w-5 ${card.color}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">{card.label}</p>
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400 mt-1" />
                ) : (
                  <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-6">
        <div className="flex overflow-x-auto border-b border-slate-200">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-brand text-brand"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              <tab.Icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "overview" && (
          <TrustOverviewTab
            bankAccountId={selectedAccountId}
            onRefresh={loadSummary}
          />
        )}
        {activeTab === "receipts" && (
          <TrustReceiptsTab
            bankAccountId={selectedAccountId}
            onRefresh={loadSummary}
          />
        )}
        {activeTab === "disbursements" && (
          <TrustDisbursementsTab
            bankAccountId={selectedAccountId}
            onRefresh={loadSummary}
          />
        )}
        {activeTab === "ledger" && (
          <TrustLedgerTab bankAccountId={selectedAccountId} />
        )}
      </div>
    </>
  );
};

/** Standalone page wrapper  -  kept for backward compat / direct route */
const TrustPage: React.FC = () => (
  <div className="min-h-screen" style={{ backgroundColor: "#F4F6F8" }}>
    <div className="max-w-[1600px] mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Trust Accounting</h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage client trust funds, deposits, disbursements, and compliance
        </p>
      </div>
      <TrustAccountsContent />
    </div>
  </div>
);

export default TrustPage;
